import type { EpisodeSeasonOrder } from './episodeIndex'

export type CatalogShow = { series: string; episode_count: number; season_count: number }
export type CatalogFilter = 'relationships' | 'people' | 'episodes'
export type CatalogResults = {
  people?: Array<Record<string, unknown>>
  relationships?: Array<Record<string, unknown>>
  episodes?: Array<Record<string, unknown>>
}
export type CatalogPickerRequest = {
  filter: CatalogFilter
  query: string
  series: string | null
  order: EpisodeSeasonOrder
  offset: number
  limit: number
}
export type CatalogPickerPage = { results: CatalogResults; nextOffset: number; hasMore: boolean }

const showLabels: Record<string, string> = {
  '90 Day Fiancé': '90 Day Fiancé',
  '90 Day Fiancé: Before the 90 Days': 'Before the 90 Days',
  '90 Day Fiancé: Happily Ever After?': 'Happily Ever After',
  '90 Day Fiancé: The Other Way': 'The Other Way',
  '90 Day: Hunt for Love': 'Hunt for Love',
  '90 Day: The Last Resort': 'The Last Resort',
  '90 Day: The Single Life': 'The Single Life',
  'Darcey & Stacey': 'Darcey & Stacey',
  'The Family Chantel': 'The Family Chantel',
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null
}

function validSeries(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && Array.from(value).length <= 160 && !/[\u0000-\u001f\u007f]/.test(value)
}

export function catalogShowLabel(series: string): string { return showLabels[series] ?? series }

export function catalogShowsFromPayload(payload: unknown): CatalogShow[] {
  const rows = record(payload)?.shows
  if (!Array.isArray(rows)) throw new Error('The show list could not be verified. Please retry.')
  const known = new Set<string>()
  return rows.map(value => {
    const row = record(value)
    if (!row || !validSeries(row.series) || known.has(row.series)
      || !Number.isSafeInteger(row.episode_count) || Number(row.episode_count) < 1
      || !Number.isSafeInteger(row.season_count) || Number(row.season_count) < 1) {
      throw new Error('The show list could not be verified. Please retry.')
    }
    known.add(row.series)
    return { series: row.series, episode_count: Number(row.episode_count), season_count: Number(row.season_count) }
  })
}

// No unscoped episode request is issued by either the tag picker or the library.
export function catalogPickerParams(request: CatalogPickerRequest): URLSearchParams | null {
  if (request.filter === 'episodes' && request.series === null) return null
  if (!Number.isSafeInteger(request.offset) || request.offset < 0
    || !Number.isSafeInteger(request.limit) || request.limit < 1 || request.limit > 50) throw new Error('Invalid catalog page')
  const params = new URLSearchParams({ q: request.query, type: request.filter, limit: String(request.limit), offset: String(request.offset) })
  if (request.filter === 'episodes') {
    if (!validSeries(request.series)) throw new Error('Choose a valid show first')
    params.set('series', request.series)
    params.set('sort', `season-${request.order}`)
  }
  return params
}

export function catalogPickerPageFromPayload(payload: unknown, request: CatalogPickerRequest): CatalogPickerPage {
  const root = record(payload)
  const results = record(root?.results)
  const pagination = record(root?.pagination)
  if (!results || !pagination || typeof pagination.has_more !== 'boolean'
    || !Number.isSafeInteger(pagination.next_offset) || Number(pagination.next_offset) < 0) {
    throw new Error('The catalog page could not be verified. Please retry.')
  }
  const keys = [request.filter] as const
  const picked: CatalogResults = {}
  for (const key of keys) {
    const rows = results[key]
    if (!Array.isArray(rows) || rows.some(row => !record(row))) throw new Error('The catalog page could not be verified. Please retry.')
    picked[key] = rows as Array<Record<string, unknown>>
  }
  if (request.filter === 'episodes') {
    if (!validSeries(request.series) || picked.episodes?.some(row => row.series !== request.series)) {
      throw new Error('The selected show’s episodes could not be verified. Please retry.')
    }
  }
  const hasMore = pagination.has_more
  const nextOffset = Number(pagination.next_offset)
  if (hasMore && nextOffset <= request.offset) throw new Error('The next catalog page could not be verified. Please retry.')
  return { results: picked, nextOffset, hasMore }
}

export async function fetchCatalogPickerPage(request: CatalogPickerRequest, signal: AbortSignal): Promise<CatalogPickerPage> {
  const params = catalogPickerParams(request)
  if (!params) throw new Error('Choose a show to see episodes.')
  const response = await fetch(`/api/catalog?${params.toString()}`, { credentials: 'same-origin', cache: 'no-store', signal })
  if (!response.ok) throw new Error('The index could not load. Please retry.')
  return catalogPickerPageFromPayload(await response.json(), request)
}
